// ./src/hooks/useTrackingFilter.ts
//
// Smart client-side point filter for live GPS tracking.
// Receives every raw GPS reading from useGpsTracker and decides
// whether to keep it based on distance, heading change, speed change,
// and elapsed time thresholds.
//
// Kept points are assigned a monotonically increasing `seq` number
// and the current `segmentId`, then passed to the `onPointKept` callback.
//
// This dramatically reduces the number of points sent to the server
// (typically 70-90% reduction) while preserving route geometry —
// turns, curves, stops, and speed changes are all captured.
//
// All internal state is stored in refs (not React state) to avoid
// unnecessary re-renders on every GPS reading (1-3 per second).

import { useRef, useCallback } from "react";
import { type GpsReading } from "./useGpsTracker";
import { type BufferedTrackingPoint } from "@/contexts/TrackingStorageContext/TrackingStorageContext";
import {
  haversineDistance,
  bearingDelta,
  type GeoPoint,
} from "@/utils/trackingGeoUtils";
import { FILTER_THRESHOLDS } from "@/config/trackingConstants";

// ============================================
// Types
// ============================================

export interface UseTrackingFilterProps {
  /**
   * Called when a raw GPS reading passes the filter and should be
   * buffered for upload. The point has been assigned a seq and segmentId.
   *
   * Must be a stable reference (useCallback or ref-based).
   */
  onPointKept: (point: BufferedTrackingPoint) => void;
}

export interface UseTrackingFilterReturn {
  /**
   * Feed a raw GPS reading into the filter.
   * Call this from the useGpsTracker onReading callback.
   * The filter decides whether to keep or discard the point.
   */
  processReading: (reading: GpsReading) => void;

  /**
   * Force-keep the most recent raw reading as a segment end point.
   * Called before pause to ensure the last position is captured,
   * even if it wouldn't normally pass the filter thresholds.
   *
   * No-op if no raw reading has been received yet.
   *
   * @returns The forced point, or null if no reading was available
   */
  forceKeepLast: () => BufferedTrackingPoint | null;

  /**
   * Reset the filter's internal state for a new segment.
   * Called when tracking resumes after a pause — the first point
   * in the new segment will always be kept.
   *
   * @param segmentId - The new segment ID (from server response)
   * @param nextSeq - The next seq number to assign
   */
  resetForNewSegment: (segmentId: number, nextSeq: number) => void;

  /**
   * Full reset — clears all internal state.
   * Called when tracking is started fresh or discarded.
   *
   * @param segmentId - Starting segment ID (usually 0)
   * @param nextSeq - Starting seq number (usually 0)
   */
  reset: (segmentId: number, nextSeq: number) => void;

  /**
   * Restore filter state from a persisted session.
   * Called on app restart to resume from where we left off.
   *
   * @param segmentId - The restored segment ID
   * @param nextSeq - The restored next seq number
   */
  restore: (segmentId: number, nextSeq: number) => void;

  /**
   * Get the current next seq value (for persistence).
   */
  getNextSeq: () => number;
}

// ============================================
// Internal State Shape
// ============================================

interface FilterState {
  /** Current segment counter */
  segmentId: number;
  /** Next seq to assign to a kept point */
  nextSeq: number;
  /** The most recently KEPT point's lat/lng */
  lastKeptLat: number | null;
  lastKeptLng: number | null;
  /** Heading at the last kept point (degrees, or null) */
  lastKeptHeading: number | null;
  /** Speed at the last kept point (m/s, or null) */
  lastKeptSpeed: number | null;
  /** Timestamp of the last kept point (ms since epoch) */
  lastKeptTimestamp: number | null;
  /** The most recent raw reading (kept or not) — used by forceKeepLast */
  lastRawReading: GpsReading | null;
  /** Whether the last raw reading was already kept (avoid double-keep in forceKeepLast) */
  lastRawWasKept: boolean;
}

const createInitialState = (
  segmentId: number,
  nextSeq: number
): FilterState => ({
  segmentId,
  nextSeq,
  lastKeptLat: null,
  lastKeptLng: null,
  lastKeptHeading: null,
  lastKeptSpeed: null,
  lastKeptTimestamp: null,
  lastRawReading: null,
  lastRawWasKept: false,
});

// ============================================
// Helper
// ============================================

/**
 * Convert a GpsReading to a BufferedTrackingPoint with assigned seq and segmentId.
 */
const readingToBufferedPoint = (
  reading: GpsReading,
  seq: number,
  segmentId: number
): BufferedTrackingPoint => ({
  lat: reading.lat,
  lng: reading.lng,
  altitude: reading.altitude,
  speed: reading.speed,
  heading: reading.heading,
  accuracy: reading.accuracy,
  recordedAt: new Date(reading.timestamp).toISOString(),
  seq,
  segmentId,
});

// ============================================
// Hook
// ============================================

/**
 * Smart point filter that reduces GPS readings by 70-90% while
 * preserving route geometry.
 *
 * A raw GPS reading is KEPT if ANY of these conditions are true:
 * 1. First point in the segment (no previous kept point)
 * 2. Distance from last kept point > 20m
 * 3. Heading changed > 12° since last kept point
 * 4. Speed delta > 3 m/s (~10 km/h) since last kept point
 * 5. Time elapsed > 30 seconds since last kept point
 *
 * Additionally, `forceKeepLast()` can be called before a pause
 * to ensure the final position is captured.
 *
 * @example
 * ```tsx
 * const handlePointKept = useCallback((point: BufferedTrackingPoint) => {
 *   // Add to upload buffer
 *   addToBuffer(point);
 * }, []);
 *
 * const { processReading, forceKeepLast, reset } = useTrackingFilter({
 *   onPointKept: handlePointKept,
 * });
 *
 * // Feed raw readings from GPS tracker
 * const handleGpsReading = useCallback((reading: GpsReading) => {
 *   processReading(reading);
 * }, [processReading]);
 *
 * // Before pausing
 * forceKeepLast();
 * ```
 */
export const useTrackingFilter = ({
  onPointKept,
}: UseTrackingFilterProps): UseTrackingFilterReturn => {
  // Store callback in ref to avoid recreating processReading
  const onPointKeptRef = useRef(onPointKept);
  onPointKeptRef.current = onPointKept;

  // All filter state in a single ref — no React state, no re-renders
  const stateRef = useRef<FilterState>(createInitialState(0, 0));

  /**
   * Core filter logic. Returns true if the reading should be kept.
   */
  const shouldKeep = useCallback((reading: GpsReading): boolean => {
    const state = stateRef.current;

    // Condition 1: First point in the segment
    if (state.lastKeptLat === null || state.lastKeptLng === null) {
      return true;
    }

    const lastKept: GeoPoint = {
      lat: state.lastKeptLat,
      lng: state.lastKeptLng,
    };
    const current: GeoPoint = { lat: reading.lat, lng: reading.lng };

    // Condition 2: Distance threshold
    const distance = haversineDistance(lastKept, current);
    if (distance > FILTER_THRESHOLDS.DISTANCE_M) {
      return true;
    }

    // Condition 3: Heading change
    // Only check if both the current reading and last kept point have heading
    if (
      reading.heading != null &&
      state.lastKeptHeading != null
    ) {
      const headingChange = bearingDelta(state.lastKeptHeading, reading.heading);
      if (headingChange > FILTER_THRESHOLDS.HEADING_DEG) {
        return true;
      }
    }

    // Condition 4: Speed change
    if (
      reading.speed != null &&
      state.lastKeptSpeed != null
    ) {
      const speedDelta = Math.abs(reading.speed - state.lastKeptSpeed);
      if (speedDelta > FILTER_THRESHOLDS.SPEED_MPS) {
        return true;
      }
    }

    // Condition 5: Time elapsed
    if (state.lastKeptTimestamp != null) {
      const elapsed = reading.timestamp - state.lastKeptTimestamp;
      if (elapsed > FILTER_THRESHOLDS.MAX_INTERVAL_MS) {
        return true;
      }
    }

    return false;
  }, []);

  /**
   * Keep a point: assign seq, update state, call onPointKept.
   */
  const keepPoint = useCallback((reading: GpsReading): BufferedTrackingPoint => {
    const state = stateRef.current;
    const seq = state.nextSeq;
    const point = readingToBufferedPoint(reading, seq, state.segmentId);

    // Update filter state
    state.nextSeq = seq + 1;
    state.lastKeptLat = reading.lat;
    state.lastKeptLng = reading.lng;
    state.lastKeptHeading = reading.heading;
    state.lastKeptSpeed = reading.speed;
    state.lastKeptTimestamp = reading.timestamp;
    state.lastRawWasKept = true;

    onPointKeptRef.current(point);

    return point;
  }, []);

  /**
   * Process a raw GPS reading through the filter.
   */
  const processReading = useCallback(
    (reading: GpsReading): void => {
      const state = stateRef.current;

      // Always track the latest raw reading (for forceKeepLast)
      state.lastRawReading = reading;
      state.lastRawWasKept = false;

      if (shouldKeep(reading)) {
        keepPoint(reading);
      }
    },
    [shouldKeep, keepPoint]
  );

  /**
   * Force-keep the last raw reading (for segment-end before pause).
   */
  const forceKeepLast = useCallback((): BufferedTrackingPoint | null => {
    const state = stateRef.current;

    // Nothing to force-keep
    if (!state.lastRawReading) {
      return null;
    }

    // Already kept — don't duplicate
    if (state.lastRawWasKept) {
      return null;
    }

    return keepPoint(state.lastRawReading);
  }, [keepPoint]);

  /**
   * Reset for a new segment (after resume).
   * Clears the "last kept" references so the first point in the
   * new segment is always kept, but preserves the raw reading ref.
   */
  const resetForNewSegment = useCallback(
    (segmentId: number, nextSeq: number): void => {
      const state = stateRef.current;
      state.segmentId = segmentId;
      state.nextSeq = nextSeq;
      state.lastKeptLat = null;
      state.lastKeptLng = null;
      state.lastKeptHeading = null;
      state.lastKeptSpeed = null;
      state.lastKeptTimestamp = null;
      // Keep lastRawReading — it may be useful for distance accumulator
      state.lastRawWasKept = false;
    },
    []
  );

  /**
   * Full reset — clear everything.
   */
  const reset = useCallback((segmentId: number, nextSeq: number): void => {
    stateRef.current = createInitialState(segmentId, nextSeq);
  }, []);

  /**
   * Restore from persisted session state.
   * Same as resetForNewSegment — we don't persist the last kept point,
   * so the first reading after restore will always be kept.
   */
  const restore = useCallback(
    (segmentId: number, nextSeq: number): void => {
      resetForNewSegment(segmentId, nextSeq);
    },
    [resetForNewSegment]
  );

  /**
   * Get current next seq for session persistence.
   */
  const getNextSeq = useCallback((): number => {
    return stateRef.current.nextSeq;
  }, []);

  return {
    processReading,
    forceKeepLast,
    resetForNewSegment,
    reset,
    restore,
    getNextSeq,
  };
};